<template>
  <movie-carousel
    v-if="section.type === SECTION_TYPES.POSTERS"
    :id="section.id"
    :type="section.config.posterCarouselType || 'current'"
    :title-class-ids="section.titleClassIds"
    :title="section.config.showHeading ? section.name : null"
    :heading-color="section.config.headingColor"
    :heading-color-hex="section.config.headingColorHex"
    :heading-font-size="section.config.headingFontSize"
    :heading-type="section.config.headingType"
    :heading-background-image-url="section.headingBackgroundImageUrl"
    :heading-background-image-repeat="section.config.headingBackgroundImageRepeat"
    :heading-align="section.config.headingAlign"
    :number-of-movies-on-show="section.config.posterCarouselNumberOfMoviesOnShow"
    :show-filter="section.config.posterCarouselShowFilter"
    :controls-visible="section.config.posterCarouselControlsVisibile"
    :controls-background-color="section.config.posterCarouselControlsBackgroundColor"
    :controls-background-color-hex="section.config.posterCarouselControlsBackgroundColorHex"
    :autoplay="section.config.posterCarouselAutoplay"
    :delay-in-seconds="section.config.posterCarouselDelayInSeconds"
    :pagination-visible="section.config.posterCarouselPaginationVisible"
    :has-customised-slides-per-row="configureMaxSlidesPerView"
    :slides-per-row="section.config.posterCarouselMaxSlidesPerView"
    :has-customised-mobile-slides-per-row="configureMobileMaxSlidesPerView && configureMaxSlidesPerView"
    :mobile-slides-per-row="section.config.posterCarouselMobileMaxSlidesPerView"
    :spacing="section.config.spacing"
    :use-banner="section.config.useBanner"
    :show-user-buttons="section.config.posterCarouselShowUserButtons"
    :show-trailer-button="section.config.posterCarouselShowTrailerButton"
    :show-movie-title="section.config.posterCarouselShowMovieTitle"
    :show-movie-rating="section.config.posterCarouselShowMovieRating"
    :show-movie-genre="section.config.posterCarouselShowMovieGenre"
    :show-title-class="section.config.posterCarouselShowTitleClass"
    :show-coming-soon-date="section.config.showComingSoonDate"
    :clean-image="section.config.posterCarouselCleanImage"
    :font-size="section.config.posterCarouselFontSize"
    :text-wrapped="section.config.posterCarouselTextWraped"
    :text-centred="section.config.posterCarouselTextCenter"
    :light-text="section.config.posterCarouselLightText"
    :border-radius="section.config.borderRadius"
    :shadow="section.config.shadow"
    :width-mode="section.config.widthMode"
    :is-phone-preview="isPhonePreview"
    :is-mobile-view="isPhonePreview || $q.screen.lt.sm"
  />
  <showtimes-list
    v-else-if="section.type === SECTION_TYPES.SHOWTIMES"
    :id="section.id"
    :banner-background="section.config.bannerBackground"
    :banner-brightness="section.config.bannerBrightness"
    :banner-opacity="section.config.bannerOpacity"
    :border-radius="section.config.borderRadius"
    :clean-image="section.config.cleanImage"
    :initial-title-class-ids="section.titleClassIds"
    :initial-movie-ids="section.movieIds"
    :initial-showing-ids="section.showingIds"
    :initial-site-ids="initialSiteIds"
    :shadow="section.config.shadow"
    :show-attendance-levels="section.config.showAttendanceLevels"
    :show-button-progress-bar="section.config.showtimesListButtonUseProgressBar && !showtimesWidgetShowShowingFromMultipleSites"
    :show-button-progress-bar-in-grid-view="section.config.showtimesGridButtonUseProgressBar && !showtimesWidgetShowShowingFromMultipleSites"
    :show-class="section.config.showClass"
    :show-title="section.config.showShowtimesWidgetTitle"
    :heading-color="section.config.headingColor"
    :heading-color-hex="section.config.headingColorHex"
    :heading-font-size="section.config.headingFontSize"
    :heading-type="section.config.headingType"
    :heading-background-image-url="section.headingBackgroundImageUrl"
    :heading-background-image-repeat="section.config.headingBackgroundImageRepeat"
    :heading-align="section.config.headingAlign"
    :initial-date="section.config.date"
    :show-date-filter="section.config.showShowtimesWidgetDateFilter"
    :showtimes-site-selector-types="section.config.showtimesWidgetSiteSelectorDisplayTypes"
    :flatten-zone-groups="section.config.showtimesWidgetFlattenZoneGroups"
    :showtimes-grid-button-use-button-drawer="section.config.showtimesGridButtonUseButtonDrawer"
    :showtimes-list-button-type="section.config.showtimesListButtonType"
    :button-type="section.config.showtimesButtonType"
    :button-color="section.config.showtimesButtonColor"
    :button-color-hex="section.config.showtimesButtonColorHex"
    :button-border-radius="section.config.showtimesButtonBorderRadius"
    :show-movie-image="section.config.showtimesListShowMovieImage"
    :show-movie-synopsis="section.config.showtimesListShowMovieSynopsis"
    :movie-synopsis-wrapped="section.config.showtimesListMovieSynopsisWrapped"
    :truncated-line-count="section.config.truncatedLineCount"
    :mobile-truncated-line-count="section.config.mobileTruncatedLineCount"
    :showtimes-grid-button-type="section.config.showtimesGridButtonType"
    :showtimes-grid-padding="section.config.showtimesGridPadding"
    :showtimes-blocks-background-color="section.config.showtimesBlocksBackgroundColor"
    :showtimes-blocks-background-color-hex="section.config.showtimesBlocksBackgroundColorHex"
    :showtimes-blocks-text-light="section.config.showtimesBlocksTextLight"
    :showtimes-view-type="section.config.showtimesViewType"
    :slider-controls-visible="section.config.showtimesSliderControlsVisible"
    :slider-pagination-visible="section.config.showtimesSliderPaginationVisible"
    :slider-autoplay="section.config.showtimesSliderAutoplay"
    :slider-paginate-grouped-slides="section.config.showtimesSliderPaginateGroupedSlides"
    :slider-delay-in-seconds="section.config.showtimesSliderDelayInSeconds"
    :slider-gutter="section.config.showtimesSliderGutter"
    :slider-per-view="section.config.showtimesSliderPerView"
    :grid-per-row="section.config.showtimesGridPerRow"
    :showtimes-display-type="section.config.showtimesDisplayType"
    :showtimes-display-type-allow-toggle="section.config.showtimesDisplayTypeAllowToggle"
    :showtimes-allow-all="section.config.showtimesAllowAll && section.config.showtimesDatePickerType === 'toggle'"
    :showtimes-date-picker-type="section.config.showtimesDatePickerType"
    :show-date-heading="section.config.showDateHeading"
    :show-showing-badge="section.config.showShowingBadge"
    :is-showtimes-dark-mode="section.config.isShowtimesDarkMode || $q.dark.isActive"
    :show-trailer-button="section.config.showTrailerButton"
    :enable-leaving-soon-banner="section.config.enableLeavingSoonBanner"
    :solid-showing-badges="section.config.solidShowingBadges"
    :title="section.config.showtimesWidgetTitle"
    :use-banner="section.config.useBanner"
    :use-class-filter="section.config.useClassFilter"
    :match-all-showing-badges="section.config.matchAllShowingBadges"
    :filter-showing-badge-ids="section.filterShowingBadgeIds"
    :filter-option-showing-badge-ids="section.filterOptionShowingBadgeIds"
    :is-phone-preview="isPhonePreview"
    :is-mobile-view="isPhonePreview || $q.screen.lt.sm"
    @select-movie="movie => selectMovie(movie)"
    @select-site="siteId => selectSite(siteId, false)"
    @select-showing="selectShowing"
    @show-site-selector-drawer="$emit('show-site-selector-drawer')"
  />
  <news-carousel
    v-else-if="section.type === SECTION_TYPES.NEWS && section.config.newsCarousel && publishedNewsItems.data.length > 0"
    :id="section.id"
    :title="section.config.newsCarousel.showHeading ? section.name : null"
    :heading-color="section.config.headingColor"
    :heading-color-hex="section.config.headingColorHex"
    :heading-font-size="section.config.headingFontSize"
    :heading-type="section.config.headingType"
    :heading-background-image-url="section.headingBackgroundImageUrl"
    :heading-background-image-repeat="section.config.headingBackgroundImageRepeat"
    :heading-align="section.config.headingAlign"
    :autoplay="section.config.newsCarousel.autoplay"
    :background-color-hex="section.config.newsCarousel.backgroundColorHex"
    :background-color="section.config.newsCarousel.backgroundColor"
    :border-radius="section.config.newsCarousel.borderRadius"
    :button-border-radius="section.config.newsCarousel.buttonBorderRadius"
    :button-color-hex="section.config.newsCarousel.buttonColorHex"
    :button-color="section.config.newsCarousel.buttonColor"
    :button-type="section.config.newsCarousel.buttonType"
    :controls-visible="section.config.newsCarousel.controlsVisible"
    :delay-in-seconds="section.config.newsCarousel.delayInSeconds"
    :has-partial-slides="section.config.newsCarousel.hasPartialSlide"
    :date-type="section.config.newsCarousel.dateType"
    :news-carousel-type="section.config.newsCarousel.type"
    :news-item-class-ids="section.newsItemClassIds"
    :keep-image-ratio="section.config.newsCarousel.newsItemKeepImageRatio"
    :news-items="publishedNewsItems.data"
    :pagination-visible="section.config.newsCarousel.paginationVisible"
    :shadow="section.config.newsCarousel.sliderShadow"
    :show-item-button="section.config.newsCarousel.showItemButton"
    :show-item-class="section.config.newsCarousel.showItemClass"
    :show-item-date="section.config.newsCarousel.showItemDate"
    :show-item-description="section.config.newsCarousel.showItemDescription"
    :show-item-subtitle="section.config.newsCarousel.showItemSubtitle"
    :show-item-title="section.config.newsCarousel.showItemTitle"
    :slider-allow-loop="section.config.newsCarousel.sliderAllowLoop"
    :slider-gutter="section.config.newsCarousel.sliderGutter"
    :slider-paginate-grouped-slides="section.config.newsCarousel.sliderPaginateGroupedSlides"
    :slides-per-view="section.config.newsCarousel.slidesPerView"
    :text-centred="section.config.newsCarousel.isTextCentred"
    :text-light="section.config.newsCarousel.isTextLight"
    :text-position="section.config.newsCarousel.textPosition"
    :width-mode="section.config.widthMode"
    :is-phone-preview="isPhonePreview"
    :is-mobile-view="isPhonePreview || $q.screen.lt.sm"
  />
  <explore-news
    v-else-if="section.type === SECTION_TYPES.EXPLORE_NEWS && publishedNewsItems.data.length > 0"
    :heading-color="section.config.headingColor"
    :heading-color-hex="section.config.headingColorHex"
    :heading-font-size="section.config.headingFontSize"
    :heading-type="section.config.headingType"
    :heading-background-image-url="section.headingBackgroundImageUrl"
    :heading-background-image-repeat="section.config.headingBackgroundImageRepeat"
    :heading-align="section.config.headingAlign"
    :title="section.config.showExploreNewsHeading ? section.name : null"
    :news-items="publishedNewsItems.data"
    :news-items-class-ids="section.newsItemClassIds"
    :initial-news-item-class-id="section.initialNewsItemClassId"
    :show-news-item-classes="section.config.showNewsItemClasses"
    :show-explore-news-item-banner="section.config.showExploreNewsItemBanner"
    :show-explore-news-item-title="section.config.showExploreNewsItemTitle"
    :show-explore-news-item-subtitle="section.config.showExploreNewsItemSubtitle"
    :show-explore-news-item-description="section.config.showExploreNewsItemDescription"
    :show-explore-news-item-button="section.config.showExploreNewsItemButton"
    :show-explore-news-item-date="section.config.showExploreNewsItemDate"
    :show-explore-news-item-class-names="section.config.showExploreNewsItemClassNames"
    :news-date-type="section.config.exploreNewsItemDateType"
    :number-of-news-on-show="section.config.exploreNewsNumberOfNewsOnShow"
    :news-per-row="section.config.exploreNewsMaxItemPerRow"
    :padding="section.config.exploreNewsItemPadding"
    :border-radius="section.config.exploreNewsItemBorderRadius"
    :shadow="section.config.exploreNewsItemShadow"
    :background-color="section.config.exploreNewsItemBackgroundColor"
    :background-color-hex="section.config.exploreNewsItemBackgroundColorHex"
    :text-centred="section.config.isExploreNewsItemTextCentre"
    :is-explore-news-item-text-light="section.config.isExploreNewsItemTextLight"
    :keep-image-ratio="section.config.exploreNewsItemKeepImageRatio"
    :is-phone-preview="isPhonePreview"
    :is-mobile-view="isPhonePreview || $q.screen.lt.sm"
  />
  <hero-image
    v-else-if="section.type === SECTION_TYPES.HERO_IMAGE"
    :image-url="section.heroImageUrl"
    :image-height="section.config.heroImageHeight"
    :image-position="section.config.heroImagePosition"
    :hero-image-alt="section.name"
    :action-url="section.heroImageActionUrl"
    :is-phone-preview="isPhonePreview"
    :is-mobile-view="isPhonePreview || $q.screen.lt.sm"
  />
  <explore-movies
    v-else-if="section.type === SECTION_TYPES.EXPLORE_MOVIES && section.config"
    :heading-color="section.config.headingColor"
    :heading-color-hex="section.config.headingColorHex"
    :heading-font-size="section.config.headingFontSize"
    :heading-type="section.config.headingType"
    :heading-background-image-url="section.headingBackgroundImageUrl"
    :heading-background-image-repeat="section.config.headingBackgroundImageRepeat"
    :heading-align="section.config.headingAlign"
    :width-mode="section.config.widthMode"
    :initial-type="section.config.exploreType"
    :initial-user-list="section.config.exploreUserList"
    :initial-display-type="section.config.exploreDisplayType"
    :explore-movies-filter-types="section.config.exploreMoviesFilterTypes"

    :filter-value="section.config.exploreFilterValue"
    :title="section.config.showExploreHeading ? section.name : null"
    :show-type="section.config.showExploreType"
    :show-display-type="section.config.showExploreDisplayType"
    :show-search="section.config.showExploreSearch"
    :show-user-button-on-posters="section.config.showUserButtonsOnExplorePosters"
    :show-trailer-button-on-posters="section.config.explorePosterShowTrailerButton"
    :show-movie-title-on-posters="section.config.explorePosterShowMovieTitle"
    :show-movie-rating="section.config.explorePosterShowMovieRating"
    :show-movie-genre="section.config.explorePosterShowMovieGenre"
    :font-size="section.config.explorePosterFontSize"
    :has-text-wrapped="section.config.explorePosterShouldWrapText"
    :has-text-centred="section.config.explorePosterHasTextCentred"
    :show-movie-showing-status-on-posters="section.config.explorePosterShowMovieShowingStatus"
    :clean-image-on-posters="section.config.explorePosterHasCleanImage"
    :explore-using-dark-mode="section.config.exploreUsingDarkMode || $q.dark.isActive"

    :use-banner="section.config.useBanner && $_.isEmpty(section.exploreFeaturedTitleClassIds) && $_.isEmpty(section.exploreFeaturedShowingBadgeIds)"
    :posters-per-row="section.config.explorePosterMaximumPostersPerRow"
    :mobile-posters-per-row="section.config.explorePosterMobileMaximumPostersPerRow"
    :poster-padding="section.config.explorePosterPadding"
    :poster-border-radius="section.config.explorePosterBorderRadius"
    :show-user-buttons="section.config.explorePosterShowUserButtons"
    :shadow="section.config.shadow"

    :poster-background-color="section.config.explorePosterBackgroundColor"
    :poster-background-color-hex="section.config.explorePosterBackgroundColorHex"
    :poster-featured-background-color="section.config.explorePosterFeaturedBackgroundColor"
    :poster-featured-background-color-hex="section.config.explorePosterFeaturedBackgroundColorHex"

    :show-movie-synopsis="section.config.explorePosterShowMovieSynopsis"
    :truncated-movie-synopsis="section.config.explorePosterMovieSynopsisTruncated"
    :truncated-line-count="section.config.truncatedLineCount"
    :mobile-truncated-line-count="section.config.mobileTruncatedLineCount"
    :show-coming-soon-date="section.config.showComingSoonDate"
    :button-label="section.config.explorePosterButtonLabel"
    :button-type="section.config.explorePosterButtonType"
    :button-color="section.config.explorePosterButtonColor"
    :button-color-hex="section.config.explorePosterButtonColorHex"
    :button-border-radius="section.config.explorePosterButtonBorderRadius"

    :show-title-class="section.config.exploreMovieShowTitleClass"

    :initial-title-class-ids="section.exploreTitleClassIds"
    :featured-title-class-ids="section.exploreFeaturedTitleClassIds"
    :featured-showing-badge-ids="section.exploreFeaturedShowingBadgeIds"
    :use-class-filter="section.config.useClassFilter"
    :is-phone-preview="isPhonePreview"
    :is-mobile-view="isPhonePreview || $q.screen.lt.sm"
  />
  <donation-widget
    v-else-if="section.type === SECTION_TYPES.DONATION"
    :width-mode="section.config.widthMode"
    :flat="section.config.flat"
    :square="section.config.square"
    :bg-color="section.config.backgroundColor"
    :bg-color-hex="section.config.backgroundColorHex"
    :bg-image="section.config.backgroundImage"
    :heading-color="section.config.headingColor"
    :heading-color-hex="section.config.headingColorHex"
    :heading-font-size="section.config.headingFontSize"
    :heading-type="section.config.headingType"
    :heading-background-image-url="section.headingBackgroundImageUrl"
    :heading-background-image-repeat="section.config.headingBackgroundImageRepeat"
    :heading-align="section.config.headingAlign"
    :order="$consumer ? $order : previewOrder"
    :title="section.config.donationTitle"
    :description="section.config.donationDescription"
    :button-text="section.config.donationButton"
    :button-type="section.config.buttonType"
    :button-color="section.config.buttonColor"
    :button-color-hex="section.config.buttonColorHex"
    :button-border-radius="section.config.buttonBorderRadius"
    :widget-bg-color="section.config.blockBackgroundColor"
    :widget-bg-color-hex="section.config.blockBackgroundColorHex"
    :widget-position="section.config.blockPosition"
    :default-amounts="section.config.donationDefaultAmounts"
    :donor-perfect-campaign-code="section.config.donorPerfectCampaignCode"
    :checkout-data="$consumer ? $donationWidgetCheckoutData : previewCheckoutData"
    :demo-mode="demoMode"
    :is-phone-preview="isPhonePreview"
    :is-mobile-view="isPhonePreview || $q.screen.lt.sm"
    :allow-donation-honoree="section.config.allowDonationHonoree"
    :donation-legal-name="section.config.donationLegalName"
    :send-tax-deductible-receipt="section.config.sendTaxDeductibleReceipt"
  />
  <showtime-quick-selector
    v-else-if="section.type === SECTION_TYPES.QUICK_SHOWTIME"

    :show-button="section.config.showButton"
    :flat="section.config.flat"
    :square="section.config.square"
    :show-icon="section.config.showIcon"

    :title-class-id="section.titleClassId"
    :showing-badge-ids="section.showingBadgeIds"

    :show-site-selector="section.config.showSiteSelector"
    :show-date-separators="section.config.showDateSeparators"

    :is-phone-preview="isPhonePreview"
    :is-mobile-view="isPhonePreview || $q.screen.lt.sm"
  />
  <voucher-sales-widget
    v-else-if="section.type === SECTION_TYPES.VOUCHERS"
    :heading-color="section.config.headingColor"
    :heading-color-hex="section.config.headingColorHex"
    :heading-font-size="section.config.headingFontSize"
    :heading-type="section.config.headingType"
    :heading-background-image-url="section.headingBackgroundImageUrl"
    :heading-background-image-repeat="section.config.headingBackgroundImageRepeat"
    :heading-align="section.config.headingAlign"
    :order="$consumer ? $order : previewOrder"
    :checkout-data="$consumer ? $voucherWidgetCheckoutData : previewCheckoutData"
    :title="section.config.voucherWidgetTitle"
    :show-title="section.config.showVoucherWidgetTitle"
    :show-cart="section.config.showVoucherWidgetCart"
    :voucher-sales="section.config.voucherSales"
    :vouchers-background-color="section.config.voucherWidgetBackgroundColor"
    :vouchers-background-color-hex="section.config.voucherWidgetBackgroundColorHex"
    :vouchers-text-color="section.config.voucherWidgetTextColor"
    :vouchers-text-color-hex="section.config.voucherWidgetTextColorHex"
    :square="section.config.square"
    :flat="section.config.flat"
    :width-mode="section.config.widthMode"
    :widget-bg-color="section.config.blockBackgroundColor"
    :widget-bg-color-hex="section.config.blockBackgroundColorHex"
    :background-color="section.config.backgroundColor"
    :background-color-hex="section.config.backgroundColorHex"
    :widget-position="section.config.blockPosition"
    :border-radius="section.config.borderRadius"
    :show-image="section.config.voucherSalesShowImage"
    :text-align="section.config.voucherSalesTextAlign"
    :is-phone-preview="isPhonePreview"
    :is-mobile-view="isPhonePreview || $q.screen.lt.sm"
  />
  <gift-card-sales-widget
    v-else-if="section.type === SECTION_TYPES.GIFT_CARDS"
    :heading-color="section.config.headingColor"
    :heading-color-hex="section.config.headingColorHex"
    :heading-font-size="section.config.headingFontSize"
    :heading-type="section.config.headingType"
    :heading-background-image-url="section.headingBackgroundImageUrl"
    :heading-background-image-repeat="section.config.headingBackgroundImageRepeat"
    :heading-align="section.config.headingAlign"
    :order="$consumer ? $order : previewOrder"
    :checkout-data="$consumer ? $giftCardWidgetCheckoutData : previewCheckoutData"
    :gift-card-sales="section.config.giftCardSales"
    :discount="section.discount"
    :title="section.config.giftCardWidgetTitle"
    :description="section.config.description"
    :show-title="section.config.showGiftCardWidgetTitle"
    :show-cart="section.config.showGiftCardWidgetCart"
    :gift-cards-classes="section.config.giftCardSalesClasses"
    :gift-cards-background-color="section.config.giftCardWidgetBackgroundColor"
    :gift-cards-background-color-hex="section.config.giftCardWidgetBackgroundColorHex"
    :gift-cards-text-color="section.config.giftCardWidgetTextColor"
    :gift-cards-text-color-hex="section.config.giftCardWidgetTextColorHex"
    :square="section.config.square"
    :flat="section.config.flat"
    :width-mode="section.config.widthMode"
    :widget-bg-color="section.config.blockBackgroundColor"
    :widget-bg-color-hex="section.config.blockBackgroundColorHex"
    :widget-position="section.config.blockPosition"
    :background-color="section.config.backgroundColor"
    :background-color-hex="section.config.backgroundColorHex"
    :show-image="section.config.giftCardSalesShowImage"
    :show-name="section.config.giftCardSalesShowName"
    :border-radius="section.config.borderRadius"
    :is-phone-preview="isPhonePreview"
    :is-mobile-view="isPhonePreview || $q.screen.lt.sm"
  />
  <marketing-signup
    v-else-if="section.type === SECTION_TYPES.MARKETING_SIGNUP"
    :background-color="section.config.backgroundColor"
    :background-color-hex="section.config.backgroundColorHex"
    :width-mode="section.config.widthMode"
    :square="section.config.square"
    :flat="section.config.flat"
    :display-type="section.config.marketingSignupDisplayType"
    :popup-trigger-type="section.config.marketingSignupPopupTriggerType"
    :delay-in-seconds="section.config.marketingSignupDisplayDelayInSeconds"
    :scroll-percentage="section.config.marketingSignupDisplayScrollPercentage"
    :popup-frequency="section.config.marketingSignupPopupFrequencyType"
    :collect-name="section.config.collectName"
    :collect-consent="section.config.collectConsent"
    :inline="section.config.inline && section.config.marketingSignupDisplayType === WIDGET_DISPLAY_TYPES.EMBED"
    :title="section.config.marketingSignupTitle"
    :show-title="section.config.marketingSignupHasTitle"
    :add-subtitle="section.config.marketingSignupHasSubtitle"
    :subtitle="section.config.marketingSignupSubtitle"
    :button-label="section.config.marketingSignupButton"
    :button-type="section.config.marketingSignupButtonType"
    :button-color="section.config.marketingSignupButtonColor"
    :button-color-hex="section.config.marketingSignupButtonColorHex"
    :button-border-radius="parseInt(section.config.marketingSignupButtonBorderRadius, 0)"
    :text-field-type="section.config.marketingSignupTextFieldType"
    :outline-text-field="section.config.marketingSignupHasTextFieldOutline"
    :use-dark-mode="section.config.marketingSignupHasLightText || $q.dark.isActive"
    :add-image="section.config.marketingSignupHasImage"
    :image-url="section.marketingSignupImageUrl"
    :image-position="section.config.marketingSignupImagePositionType"
    :demo-mode="demoMode"
    :is-phone-preview="isPhonePreview"
    :is-mobile-view="isPhonePreview || $q.screen.lt.sm"
  />
  <generic-form
    v-else-if="section.type === SECTION_TYPES.GENERIC_FORM"
    :title="section.config.title"
    :heading-color="section.config.headingColor"
    :heading-color-hex="section.config.headingColorHex"
    :heading-font-size="section.config.headingFontSize"
    :heading-type="section.config.headingType"
    :heading-background-image-url="section.headingBackgroundImageUrl"
    :heading-background-image-repeat="section.config.headingBackgroundImageRepeat"
    :heading-align="section.config.headingAlign"
    :description="section.config.description"
    :form-name="section.config.formName"
    :notify-email="section.config.notifyEmail"
    :submission-email="section.config.submissionEmail"
    :after-submit-message="section.config.afterSubmitMessage"
    :button-label="section.config.buttonLabel"
    :form-fields="section.config.formFields"
    :square="section.config.square"
    :flat="section.config.flat"
    :width-mode="section.config.widthMode"
    :form-subject-blocks="section.config.formSubjectBlocks"
    :form-from-blocks="section.config.formFromBlocks"
    :bg-color="section.config.backgroundColor"
    :bg-color-hex="section.config.backgroundColorHex"
    :button-type="section.config.buttonType"
    :button-color="section.config.buttonColor"
    :button-color-hex="section.config.buttonColorHex"
    :button-border-radius="section.config.buttonBorderRadius"
    :widget-bg-color="section.config.blockBackgroundColor"
    :widget-bg-color-hex="section.config.blockBackgroundColorHex"
    :widget-position="section.config.blockPosition"
    :preview="demoMode"
    :is-phone-preview="isPhonePreview"
    :is-mobile-view="isPhonePreview || $q.screen.lt.sm"
  />
  <consumer-footer
    v-else-if="section.type === SECTION_TYPES.FOOTER"
    :show-logo="section.config.showLogo"
    :show-contact-info="section.config.showContactInfo"
    :show-main-links="section.config.showMainLinks"
    :show-more-links="section.config.showMoreLinks"
    :social-icons-color="section.config.socialIconsColor"
    :background-color="section.config.backgroundColor"
    :footer-dark-mode="section.config.footerDarkMode || $q.dark.isActive"
    :show-social-icons="section.config.showSoicalIcons"
    :vertical-align-top="section.config.verticalAlignTop"
    :is-phone-preview="isPhonePreview"
    :is-mobile-view="isPhonePreview || $q.screen.lt.sm"
    :show-circuit-name-in-footer="section.config.showCircuitNameInFooter"
  />
  <generic-carousel
    v-else-if="section.type === SECTION_TYPES.GENERIC_CAROUSEL"
    :id="section.id"
    :carousel="section.config.carousel"
    :class="section.class"
    :shadow="section.config.shadow"
    :autoplay="section.config.autoplay"
    :delay-in-seconds="section.config.delayInSeconds"
    :square="section.config.square"
    :flat="section.config.flat"
    :keep-ratio="section.config.carousel.keepRatio"
    :clean-image="section.config.carousel.cleanImage"
    :is-phone-preview="isPhonePreview"
    :is-mobile-view="isPhonePreview || $q.screen.lt.sm"
    :heading-color="section.config.headingColor"
    :heading-color-hex="section.config.headingColorHex"
    :heading-font-size="section.config.headingFontSize"
    :heading-type="section.config.headingType"
    :heading-background-image-url="section.headingBackgroundImageUrl"
    :heading-background-image-repeat="section.config.headingBackgroundImageRepeat"
    :heading-align="section.config.headingAlign"
  />
  <site-selector
    v-else-if="section.type === SECTION_TYPES.SITE_SELECTOR"
    :background-color="section.config.backgroundColor"
    :background-color-hex="section.config.backgroundColorHex"
    :width-mode="section.config.widthMode"
    :square="section.config.square"
    :flat="section.config.flat"
    :title="section.config.siteSelectorTitle || $t('siteLocation.selectLocationPrompt')"
    :show-title="section.config.siteSelectorHasTitle"
    :title-color="section.config.siteSelectorTitleColor"
    :title-color-hex="section.config.siteSelectorTitleColorHex"
    :show-subtitle="section.config.siteSelectorHasSubtitle"
    :subtitle="section.config.siteSelectorSubtitle"
    :allow-dismiss="section.config.siteSelectorAllowDismiss"
    :button-type="section.config.siteSelectorButtonType"
    :button-color="section.config.siteSelectorButtonColor"
    :button-color-hex="section.config.siteSelectorButtonColorHex"
    :border-radius="section.config.siteSelectorBorderRadius"
    :block-background-color="section.config.siteSelectorBlockBackgroundColor"
    :block-background-color-hex="section.config.siteSelectorBlockBackgroundColorHex"
    :block-text-color="section.config.siteSelectorBlockTextColor"
    :block-text-color-hex="section.config.siteSelectorBlockTextColorHex"
    :block-text-centred="section.config.siteSelectorBlockHasTextCentred"
    :block-shadow="section.config.siteSelectorBlockShadow"
    :use-dark-mode="section.config.siteSelectorHasLightText || $q.dark.isActive"
    :show-image="section.config.siteSelectorHasImage"
    :image-url="section.siteSelectorImageUrl"
    :widget-display-type="section.config.siteSelectorWidgetDisplayType"
    :site-selectors-display-type="section.config.siteSelectorsDisplayType"
    :site-selector-items="section.config.siteSelectorItems"
    :delay-in-seconds="section.config.siteSelectorDisplayDelayInSeconds"
    :edit-mode="editMode"
    :demo-mode="demoMode"
    :is-phone-preview="isPhonePreview"
    :is-mobile-view="isPhonePreview || $q.screen.lt.sm"
  />
  <site-selector-with-zones
    v-else-if="section.type === SECTION_TYPES.SITE_SELECTOR_WITH_ZONES"
    style="min-height: 0%;"
    :title="section.config.siteSelectorTitle"
    :show-heading="section.config.siteSelectorShowHeading"
    :heading-color="section.config.headingColor"
    :heading-color-hex="section.config.headingColorHex"
    :heading-font-size="section.config.headingFontSize"
    :heading-type="section.config.headingType"
    :heading-background-image-url="section.headingBackgroundImageUrl"
    :heading-background-image-repeat="section.config.headingBackgroundImageRepeat"
    :heading-align="section.config.headingAlign"
    :initial-display-type="section.config.siteSelectorInitialDisplayType"
    :button-type="section.config.siteSelectorLocationButtonType"
    :button-color="section.config.siteSelectorLocationButtonColor"
    :button-color-hex="section.config.siteSelectorLocationButtonColorHex"
    :button-border-radius="section.config.siteSelectorLocationButtonBorderRadius"
    :field-type="section.config.siteSelectorFieldType"
    :field-outlined="section.config.siteSelectorFieldOutlined"
    :compact-view="section.config.siteSelectorCompactView"
    :default-expanded="section.config.siteSelectorDefaultExpanded"
    :background-color="section.config.backgroundColor"
    :background-color-hex="section.config.backgroundColorHex"
    :width-mode="section.config.widthMode"
    :show-site-image="section.config.siteSelectorLocationShowSiteImage"
    :show-site-description="section.config.siteSelectorLocationShowSiteDescription"
    :card-color="section.config.siteSelectorCardColor"
    :card-color-hex="section.config.siteSelectorCardColorHex"
    :card-border-radius="section.config.siteSelectorCardBorderRadius"
    :redirect-home-page="section.config.siteSelectorRedirectToHomePage"
    :is-mobile-view="isPhonePreview || $q.screen.lt.sm"
    is-widget
  />
  <!-- eslint-disable vue/no-v-html -->
  <!-- v-html produced by tool -->
  <div
    v-else-if="section.type === SECTION_TYPES.CUSTOM || section.type === SECTION_TYPES.RICH_TEXT"
    style="width: 100%"
    class="main-content custom-page"
    @click="delegateHrefToRouter"
    v-html="processedCustomContent"
  />
  <!-- eslint-enable vue/no-v-html -->
</template>

<script>
import CheckoutData from 'src/models/order/CheckoutData'
import Order from 'src/models/order/Order'
import Section, { SECTION_TYPES, WIDGET_DISPLAY_TYPES } from 'src/models/page/Section.js'
import ExploreNews from 'src/modules/consumer/components/ExploreNews'
import HeroImage from 'src/modules/consumer/components/HeroImage'
import MovieCarousel from 'src/modules/consumer/components/MovieCarousel'
import NewsCarousel from 'src/modules/consumer/components/NewsCarousel'
import ShowtimeQuickSelector from 'src/modules/consumer/components/ShowtimeQuickSelector'
import ShowtimesList from 'src/modules/consumer/components/ShowtimesList'
import ConsumerFooter from 'src/modules/consumer/layouts/ConsumerFooter'
import ExploreMovies from 'src/modules/consumer/pages/ExploreMovies'
import { defineAsyncComponent } from 'vue'

// NOTE: When adding component imports, consider using dynamic imports to reduce bundle size.
// If a component is expected to be commonly used on the homepage, it's fine to import it statically
// as above. But if a component is expected to be uncommonly used, or not immediately used on the
// homepage, prefer to use dynamic imports to lazy load them for better first page load performance.

export default {
  name: 'SectionComponent',
  components: {
    ConsumerFooter,
    DonationWidget: defineAsyncComponent(() => import('src/modules/checkout/components/DonationWidget')),
    ExploreMovies,
    ExploreNews,
    GenericCarousel: defineAsyncComponent(() => import('src/modules/consumer/components/GenericCarousel')),
    GenericForm: defineAsyncComponent(() => import('src/modules/consumer/components/GenericForm')),
    GiftCardSalesWidget: defineAsyncComponent(() => import('src/modules/consumer/components/GiftCardSalesWidget')),
    HeroImage,
    MarketingSignup: defineAsyncComponent(() => import('src/modules/consumer/components/MarketingSignup')),
    MovieCarousel,
    NewsCarousel,
    ShowtimeQuickSelector,
    ShowtimesList,
    SiteSelector: defineAsyncComponent(() => import('src/modules/consumer/components/site-selector/SiteSelector')),
    SiteSelectorWithZones: defineAsyncComponent(() => import('src/modules/consumer/components/site-selector/SiteSelectorWithZones')),
    VoucherSalesWidget: defineAsyncComponent(() => import('src/modules/consumer/components/VoucherSalesWidget'))
  },
  props: {
    section: {
      type: Section,
      required: true
    },
    isPhonePreview: {
      type: Boolean,
      required: false,
      default: false
    },
    demoMode: {
      type: Boolean,
      required: false,
      default: false
    },
    editMode: {
      type: Boolean,
      required: false,
      default: false
    },
    emitActions: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['show-site-selector-drawer', 'select-movie', 'select-showing'],
  data () {
    return {
      selectedSiteId: null,
      previewCheckoutData: this.$consumer ? null : new CheckoutData(),
      previewOrder: this.$consumer
        ? null
        : new Order({}, {
          orderItems: {
            voucherPurchase: {
              voucherType: true,
              card: 'default'
            }
          }
        })
    }
  },
  computed: {
    configureMaxSlidesPerView () {
      return !!this.section.config.posterCarouselMaxSlidesPerView && this.section.config.posterCarouselMaxSlidesPerView !== 5.2
    },
    configureMobileMaxSlidesPerView () {
      return !!this.section.config.posterCarouselMobileMaxSlidesPerView && this.section.config.posterCarouselMobileMaxSlidesPerView !== 2.2
    },
    showtimesWidgetShowSiteSelector () {
      return !_.isEmpty(this.section.config.showtimesWidgetSiteSelectorDisplayTypes) && !_.isNil(this.section.config.showtimesWidgetSiteSelectorDisplayTypes)
    },
    showtimesWidgetShowShowingFromMultipleSites () {
      return this.multipleSitesShowing && this.showtimesWidgetShowSiteSelector
    },
    initialSiteIds () {
      if (!this.multipleSitesShowing) return [this.currentSite.id]

      const selectableSites = _.filter(this.sites.data, site => !site.isLibrary && (site.consumerConfig && !site.consumerConfig.hideOnLocationSelectPage))

      // If the site selector is not shown in showtimes widget, show the current site showtimes
      if (!this.showtimesWidgetShowSiteSelector) {
        return [this.currentSite.id]
      }
      if (this.selectedSiteId) {
        return [this.selectedSiteId]
      }
      // There is no selected site ID and the current site is not selectable,
      // reset the default site to null so the UI shows the "no cinemas selected" prompt.
      const currentSiteIsSelectable = _.find(selectableSites, site => site.id === this.currentSite.id)
      return currentSiteIsSelectable ? [this.currentSite.id] : null
    },
    multipleSitesShowing () {
      return this.currentSite.consumerConfig.showMoviesAndShowingsForMultipleSites
    },
    processedCustomContent () {
      if (!this.section.content) return ''

      // Process HTML to add alt text to images missing it
      const parser = new DOMParser()
      const doc = parser.parseFromString(this.section.content, 'text/html')
      const images = doc.querySelectorAll('img:not([alt])')

      images.forEach(img => {
        const altText = `${this.$t('image.missingAltText')} ${this.section.name}`
        img.setAttribute('alt', altText)
      })

      return doc.body.innerHTML
    }
  },

  unmounted () {
    this.previewCheckoutData?.destroy()
    this.previewOrder?.destroy()
    this.previewCheckoutData = null
    this.previewOrder = null
  },
  configuration: ['publishedNewsItems', 'sites', 'currentSite'],
  created () {
    _.extend(this, { WIDGET_DISPLAY_TYPES, SECTION_TYPES })

    this.loadLastSelectedLocation()
  },
  methods: {
    loadLastSelectedLocation () {
      if (this.multipleSitesShowing) {
        const lastSelectedLocationStr = localStorage.getItem(this.$constants.localStorageKeys.lastSelectedLocation)
        let lastSelectedLocation = null

        try {
          lastSelectedLocation = lastSelectedLocationStr ? JSON.parse(lastSelectedLocationStr) : null
        } catch (error) {
          console.warn('Failed to parse lastSelectedLocation from localStorage:', error)
          return
        }

        if (lastSelectedLocation && lastSelectedLocation.siteId === this.currentSite.id) {
          // Check if the stored location has expired
          const now = Date.now()
          const storedTimestamp = lastSelectedLocation.timestamp || 0
          const isCurrentLocation = lastSelectedLocation.type === this.$constants.localStorageKeys.useCurrentLocation
          const expiresInDays = isCurrentLocation ? 1 : 7
          const expirationTime = storedTimestamp + (expiresInDays * 24 * 60 * 60 * 1000) // Convert days to milliseconds

          if (now > expirationTime) {
            // Location has expired, remove it from localStorage
            localStorage.removeItem(this.$constants.localStorageKeys.lastSelectedLocation)
            return
          }

          const lastSelectedLocationId = lastSelectedLocation.lastSelectedLocationId
          this.selectedSiteId = lastSelectedLocationId
          if (isCurrentLocation) {
            this.useCurrentLocation = true
          }
        }
      }
    },
    selectMovie (movie) {
      if (this.emitActions) {
        this.$emit('select-movie', movie)
      } else {
        this.$trackEvent(this.$customerEvents.CLICKED_MOVIE, movie.trackingData)
        if (this.multipleSitesShowing && movie.siteId !== this.currentSite.id) {
          window.location = movie.pageUrl
        } else {
          this.$router.push(movie.pagePath)
        }
      }
    },
    selectSite (siteId, getClosestLocations) {
      this.selectedSiteId = siteId

      const locationType = getClosestLocations
        ? this.$constants.localStorageKeys.useCurrentLocation
        : null

      try {
        const locationData = {
          siteId: this.currentSite.id,
          lastSelectedLocationId: siteId,
          type: locationType,
          timestamp: Date.now()
        }
        localStorage.setItem(this.$constants.localStorageKeys.lastSelectedLocation, JSON.stringify(locationData))
      } catch (error) {
        console.warn('Failed to save preferred locations to localStorage:', error)
      }
    },
    selectShowing (showing) {
      if (this.emitActions) {
        this.$emit('select-showing', showing)
      } else {
        this.$trackEvent(this.$customerEvents.CLICKED_SHOWING, {
          showing_id: showing.id,
          showing_time: this.$d(showing.siteTime, 'shortLong'),
          ...showing.movie.trackingData
        })
        if (this.$order) {
          // We shouldn't need to do  this - the routed page should be doing this for us.
          // However, the routed page is respecting the currenShowing over the routed showing id (sometimes).
          // So we're doing it here to ensure the correct showing is displayed until we can fix the underlying issue.
          this.$order.currentShowing = showing
        }

        if (this.multipleSitesShowing && showing.movie.siteId !== this.currentSite.id) {
          window.location = showing.checkoutUrl
        } else {
          this.$router.push(showing.checkoutPath)
        }
      }
    },
    delegateHrefToRouter (event) {
      // Adapted from: https://dennisreimann.de/articles/delegating-html-links-to-vue-router.html
      // ensure we use the link, in case the click has been received by a subelement
      let { target } = event
      while (target && target.tagName !== 'A') target = target.parentNode
      // Handle only links that occur inside the component and do not reference external resources.
      if (target && !target.matches("a[href*='://']") && !target.matches("a[href*='mailto:']") && target.href) {
        // some sanity checks taken from vue-router:
        // https://github.com/vuejs/vue-router/blob/dev/src/components/link.js#L106
        const { altKey, ctrlKey, metaKey, shiftKey, button, defaultPrevented } = event
        // don't handle with control keys
        if (metaKey || altKey || ctrlKey || shiftKey) return
        // don't handle when preventDefault called
        if (defaultPrevented) return
        // don't handle right clicks
        if (button !== undefined && button !== 0) return
        // don't handle if `target="_blank"`
        if (target && target.getAttribute) {
          const linkTarget = target.getAttribute('target')
          if (/\b_blank\b/i.test(linkTarget)) return
        }

        // Correct anchor links page direction
        if (target.matches("a[href^='#']")) {
          target.href = `${window.location.href.split('#')[0]}${target.hash}`
        } else if (target.matches("a[href^='/'], a[href*='#']")) {
          target.href = `${window.location.origin}${target.pathname}${target.hash}`
        }

        // If we're directing to the current page, it's probably an anchor link,
        // let the browser handle it normally.
        const url = new URL(target.href)
        if (window.location.pathname === url.pathname) {
          return
        }

        // If we're directing to a different page, let Vue router handle it.
        const to = [url.pathname, url.search || ''].join('')
        if (window.location.href !== to && event.preventDefault) {
          event.preventDefault()
          this.$router.push(to)
        }
      }
    }
  }
}
</script>
